/**
 * Plans — one source of truth for the marketing surfaces.
 *
 * Pricing.vue, Home.vue and the door landing pages all render the live plan
 * catalogue from GET /api/cluster/plans. This module holds the pure parts
 * (sorting, price lookup, formatting, door-aware emphasis) so the three
 * surfaces cannot drift, and a small in-memory cache so a visitor who moves
 * from a door to /pricing does not pay for the same read twice.
 *
 * No `@/` imports: tests run it under plain Node.
 */

export const CURRENCY_STORAGE_KEY = 'cs:currency'
export const SUPPORTED_CURRENCIES = Object.freeze(['EUR', 'USD', 'GEL'])
export const CURRENCY_SYMBOLS = Object.freeze({ EUR: '€', USD: '$', GEL: '₾' })

/** Memberships first, then workspaces by ascending price in `currency`. */
export function sortPlans(plans, currency = 'EUR') {
  return (Array.isArray(plans) ? plans : []).slice().sort((a, b) => {
    if (a.tier_type !== b.tier_type) {
      return a.tier_type === 'membership' ? -1 : 1
    }
    const ap = priceFor(a, currency)?.price || 0
    const bp = priceFor(b, currency)?.price || 0
    return ap - bp
  })
}

export function priceFor(plan, currency = 'EUR') {
  if (!plan?.prices?.length) return null
  return plan.prices.find((p) => p.currency === currency) || plan.prices[0] || null
}

export function formatAmount(price) {
  if (price === null || price === undefined) return '—'
  const num = Number(price)
  if (!Number.isFinite(num)) return String(price)
  return Number.isInteger(num) ? num.toLocaleString() : num.toFixed(2)
}

export function billingLabel(plan) {
  const period = (plan?.billing_period || 'month').toLowerCase()
  if (period === 'year' || period === 'annual' || period === 'yearly') return 'year'
  if (period === 'month' || period === 'monthly') return 'month'
  return period
}

/** The plan's stable key: `tier_key` from the catalogue, else a slug of its name. */
export function tierKeyOf(plan) {
  if (!plan) return ''
  if (plan.tier_key) return String(plan.tier_key).toLowerCase()
  return String(plan.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
}

/**
 * Door-aware emphasis. Given the sorted catalogue and a door's `plans` list
 * (leading tier_key first), split into the plans to show expanded and the
 * rest to collapse. A door with an empty list shows no cards (door E).
 *
 * Returns { emphasised: Plan[], collapsed: Plan[], lead: Plan|null }.
 */
export function emphasisePlans(plans, doorPlans) {
  const list = Array.isArray(plans) ? plans : []
  const keys = Array.isArray(doorPlans) ? doorPlans : null
  if (!keys) return { emphasised: list, collapsed: [], lead: null }
  if (keys.length === 0) return { emphasised: [], collapsed: list, lead: null }
  const byKey = new Map(list.map((p) => [tierKeyOf(p), p]))
  const emphasised = keys.map((k) => byKey.get(k)).filter(Boolean)
  const chosen = new Set(emphasised.map((p) => p.id))
  const collapsed = list.filter((p) => !chosen.has(p.id))
  return { emphasised, collapsed, lead: emphasised[0] || null }
}

/**
 * Worked cost-per-client example for door B. A Team workspace shared across
 * `clients` client environments costs `price / clients` each per month.
 * Returns null when the plan has no price in the currency.
 */
export function costPerClient(plan, clients, currency = 'EUR') {
  const p = priceFor(plan, currency)
  if (!p || !Number.isFinite(Number(p.price)) || !clients) return null
  const total = Number(p.price)
  return {
    currency: p.currency,
    total,
    clients,
    perClient: Math.round((total / clients) * 100) / 100
  }
}

// ── Cached fetch (browser side) ─────────────────────────────────────────────

let inflight = null
let cached = null
let cachedAt = 0
const TTL_MS = 5 * 60 * 1000

/**
 * Load the catalogue once per TTL through the supplied fetcher (normally
 * `clusterApi.getPlans`). Concurrent callers share the request; a failure is
 * not cached, so the next caller retries.
 */
export async function loadPlansCached(fetcher, { force = false, now = Date.now() } = {}) {
  if (!force && cached && now - cachedAt < TTL_MS) return cached
  if (!inflight) {
    inflight = Promise.resolve()
      .then(() => fetcher())
      .then((data) => {
        cached = Array.isArray(data) ? data : []
        cachedAt = Date.now()
        return cached
      })
      .finally(() => { inflight = null })
  }
  return inflight
}

/** Test seam. */
export function _resetPlansCache() {
  inflight = null
  cached = null
  cachedAt = 0
}
